import parse from 'html-react-parser';
import { v4 } from 'uuid';

/**
 * Accepte une chaine de caractères contenant du html, et retourne son équivalent JSX.
 * Le second paramètre, optionnel, permet d'injecter dynamiquement du contenu via des
 * placeholders de la forme `[key]`, qui seront remplacé par un élement react.
 *
 * Techniquement, le remplacement est fait en deux étapes :
 * - Les placeholders `[key]` sont remplacés par des balises `<code />` contenant une ref unique
 * - Lors de l'étape de parsing, les balises `<code />` sont remplacées par l'élément correspondant
 *
 * **Utilisation :**
 * ```tsx
 * const html = renderHTML('<p>Hello [firstname] [lastname] !</p>', {
 *   firstname: <strong>John</strong>,
 *   lastname: <span color="pink">Doe</span>,
 * });
 * ```
 */
const renderHTML = (html: string, replace?: Record<string, JSX.Element>) => {
  // Correspondance key => tag => node
  const keyToRef: Record<string, string> = {};
  const refToNode: Record<string, JSX.Element> = {};
  Object.entries(replace ?? {}).forEach(([key, node]) => {
    refToNode[(keyToRef[key] = v4())] = node;
  });
  // Remplacement des [key] par des <code ref="<uuid>" />
  const replaced = Object.entries(keyToRef).reduce((acc, [key, tag]) => {
    return acc.split(`[${key}]`).join(`<code ref="${tag}"></code>`);
  }, html);
  // Parse et remplace les <code/> par l'élément correspondant
  return parse(replaced, {
    replace: node => {
      if (!('tagName' in node) || node.tagName !== 'code') return undefined;
      if (!('attribs' in node) || node.attribs.ref == null) return undefined;
      return refToNode[node.attribs.ref];
    },
  });
};

export { renderHTML };
